import { IBrandTag } from '../../faire/brand/tags/IBrandTag';
import { IBrandSampleProgram } from '../../faire/samples/IBrandSampleProgram';
import { FreeShippingReason } from '../../indigofair/data/FreeShippingReason';
import { IApplicableMembershipBenefits } from '../../indigofair/data/IApplicableMembershipBenefits';
import { IBadge } from '../../indigofair/data/IBadge';
import { IBrand } from '../../indigofair/data/IBrand';
import { IBrandBanner } from '../../indigofair/data/IBrandBanner';
import { IBrandLocationInfo } from '../../indigofair/data/IBrandLocationInfo';
import { IBrandPromoCollection } from '../../indigofair/data/IBrandPromoCollection';
import { IBrandRetailerInfo } from '../../indigofair/data/IBrandRetailerInfo';
import { IBrandReviewsSummary } from '../../indigofair/data/IBrandReviewsSummary';
import { IBrandShopPreferences } from '../../indigofair/data/IBrandShopPreferences';
import { IBrandSocialMediaInfo } from '../../indigofair/data/IBrandSocialMediaInfo';
import { IFaireFundedPromo } from '../../indigofair/data/IFaireFundedPromo';
import { IFaireMoney } from '../../indigofair/data/IFaireMoney';
import { IImage } from '../../indigofair/data/IImage';
import { IInformationMessage } from '../../indigofair/data/IInformationMessage';
import { IInsiderLabel } from '../../indigofair/data/IInsiderLabel';
import { IPaginationData } from '../../indigofair/data/IPaginationData';
import { IPromoDisplayText } from '../../indigofair/data/IPromoDisplayText';
import { ISeasonYear } from '../../indigofair/data/ISeasonYear';
import { IShippingDetails } from '../../indigofair/data/IShippingDetails';
import { ISlateTextElement } from '../../indigofair/data/ISlateTextElement';
import { IUserTranslatedString } from '../../indigofair/data/IUserTranslatedString';
import { IBrandVideo } from '../../indigofair/video/IBrandVideo';

export namespace IBrandViewV2Response {
  export namespace ILeanBrand {
    /**
     * Return an instance of ILeanBrand with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<ILeanBrand>): ILeanBrand =>
      ({
        active_promo_collections: [],
        badges: [],
        story_images: [],
        tags: [],
        social_media_images: [],
        ...(partial ?? {}),
      }) as ILeanBrand;
  }

  /**
   * Sliced version of original Brand proto.
   */
  export interface ILeanBrand {
    token: string | undefined;
    active: boolean | undefined;
    is_new: boolean | undefined;
    name: string | undefined;
    profile_image: IImage | undefined;
    squared_image: IImage | undefined;
    description: string | undefined;
    is_internal: boolean | undefined;
    is_following: boolean | undefined;
    /**
     * @deprecated
     */
    has_samples: boolean | undefined;
    is_top_shop: boolean | undefined;
    made_in_state: string | undefined;
    made_in_city: string | undefined;
    based_in: string | undefined;
    based_in_state: string | undefined;
    based_in_city: string | undefined;
    ships_from_country: string | undefined;
    vacation_start_date: number | undefined;
    vacation_end_date: number | undefined;
    hide_chat: boolean | undefined;
    primary_category: keyof typeof IBrand.PrimaryCategory | undefined;
    /**
     * Replaced by activePromoCollections
     * @deprecated
     */
    active_promo_collection: IBrandPromoCollection | undefined;
    active_promo_collections: Array<IBrandPromoCollection>;
    is_highlighted_brand_for_promotional_event: boolean | undefined;
    /**
     * @deprecated
     */
    free_shipping_reason: keyof typeof FreeShippingReason | undefined;
    insider_benefits: IApplicableMembershipBenefits | undefined;
    insider_label: IInsiderLabel | undefined;
    made_in: string | undefined;
    sold_on_amazon: boolean | undefined;
    eco_friendly: boolean | undefined;
    hand_made: boolean | undefined;
    charitable: boolean | undefined;
    organic: boolean | undefined;
    women_owned: boolean | undefined;
    small_batch: boolean | undefined;
    minimum_order_amount: IFaireMoney | undefined;
    first_order_minimum_amount: IFaireMoney | undefined;
    reorder_minimum_amount: IFaireMoney | undefined;
    lead_time_days: number | undefined;
    upper_bound_lead_time_days: number | undefined;
    lower_bound_lead_time_days: number | undefined;
    shipping_time_lower_bound_days: number | undefined;
    shipping_time_upper_bound_days: number | undefined;
    estimated_ship_date_formatted: string | undefined;
    zip_code_protection_enabled: boolean | undefined;
    is_first_order: boolean | undefined;
    vacation_banner_text: string | undefined;
    banner_image: IImage | undefined;
    owner_first_name: string | undefined;
    creation_year: number | undefined;
    badges: Array<IBadge>;
    video_url: string | undefined;
    story_images: Array<IImage>;
    instagram_handle: string | undefined;
    tiktok_handle: string | undefined;
    brand_reviews_summary: IBrandReviewsSummary | undefined;
    has_new_products: boolean | undefined;
    is_apparel: boolean | undefined;
    active_products_count: number | undefined;
    tags: Array<IBrandTag>;
    social_media_images: Array<IImage>;
    instagram_followers: number | undefined;
  }

  /**
   * Return an instance of IBrandViewV2Response with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<IBrandViewV2Response>
  ): IBrandViewV2Response =>
    ({
      banners: [],
      related_search_terms: [],
      brand_videos: [],
      active_season_years: [],
      info_texts: [],
      faire_funded_promo: [],
      highlighted_apparel_seasons_for_promotional_event: [],
      promo_display_texts: [],
      ...(partial ?? {}),
    }) as IBrandViewV2Response;
}

export interface IBrandViewV2Response {
  brand: IBrandViewV2Response.ILeanBrand | undefined;
  banners: Array<IBrandBanner>;
  brand_retailer_info: IBrandRetailerInfo | undefined;
  /**
   * @deprecated
   */
  related_search_terms: Array<string>;
  shop_preferences: IBrandShopPreferences | undefined;
  brand_videos: Array<IBrandVideo>;
  brand_video_pagination_data: IPaginationData | undefined;
  active_season_years: Array<ISeasonYear>;
  eligible_for_uk_eu_brands_free_shipping: boolean | undefined;
  info_texts: Array<IInformationMessage>;
  faire_funded_promo: Array<IFaireFundedPromo>;
  is_non_returnable_partner_brand: boolean | undefined;
  brand_description: IUserTranslatedString | undefined;
  brand_made_in: IUserTranslatedString | undefined;
  /**
   * This is for providing a list of Seasons and Years associated with a Brand's RSVP feedback
   */
  highlighted_apparel_seasons_for_promotional_event: Array<ISeasonYear>;
  insider_label: IInsiderLabel | undefined;
  has_insider_free_duties: boolean | undefined;
  is_showroom_brand: boolean | undefined;
  /**
   * `true` if `catalogs` is source of truth for this brand at time of request.
   * Populated via [com.faire.catalogs.BrandIsCatalogsSourceOfTruthWireFilter]
   */
  is_catalogs_source_of_truth_for_products: boolean | undefined;
  promo_display_texts: Array<IPromoDisplayText>;
  brand_sample_program: IBrandSampleProgram | undefined;
  season_year_display_text: string | undefined;
  has_current_or_upcoming_market_promotion: boolean | undefined;
  /**
   * @deprecated
   */
  show_optimized_brand_page_copy: boolean | undefined;
  brand_location_info: IBrandLocationInfo | undefined;
  brand_social_media_info: IBrandSocialMediaInfo | undefined;
  shipping_details: IShippingDetails | undefined;
  free_shipping_brand_info_text: ISlateTextElement | undefined;
  exclusivity_cta_text: string | undefined;
  exclusivity_cta_link: string | undefined;
}
